import { WcClient } from '../wc/client';
import {
  countryColor, DEFAULT_PRIMARY, DEFAULT_SECONDARY, DEFAULT_START, FORMATION, formatBudget, KIT_COLORS,
  POS_LABEL, SQUAD_COMP, WC_BUDGET_CAP, wcCountryLimit, WC_POSITIONS, WC_SQUAD_SIZE, WC_STARTERS,
  wcTransferTeams,
} from '../wc/domain';
import { countryCounts, positionCounts, squadCost, validateWcSquad, type WcDraft } from '../wc/rules';
import type { WcLive, WcPlayer, WcPosId, WcTeam } from '../wc/types';
import type { WcSquad } from '../net/protocol';

export interface WcScreenCallbacks {
  onSave: (squad: WcSquad) => void;
  onRequestSquads: () => void;
}

export interface WcScreenData {
  youId: string;
  opponentId: string;
  squads: WcSquad[];
  /** Shield colour palette for the team editor. Defaults to KIT_COLORS;
   *  FWC passes an extended set (hot pink / purple / teal). */
  kitColors?: Array<{ name: string; hex: string }>;
  /** Budget cap floor (£m). Defaults to WC_BUDGET_CAP; FWC raises it to fit the
   *  migrated real-world teams. usableBudget is max(this, owned squad value). */
  budgetCap?: number;
}

type WcTab = 'team' | 'transfers' | 'gw' | 'league';
type WcView = 'you' | 'opp';
type WcMode = 'list' | 'pitch';
const HIT_COST = 4; // points per transfer beyond the free allowance (mirrors worker)

/** Centred cross mark for remove buttons (the × text glyph sits low in its
 *  line box, so it never looks optically centred in a small circle). */
const CROSS_SVG = '<svg class="wc-cross" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 2.5l7 7M9.5 2.5l-7 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" fill="none"/></svg>';

/** Classic floppy "save" icon for the pinned save button. */
const SAVE_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7l-4-4zm2 16H5V5h11.17L19 7.83V19zM12 12a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM6 6h9v4H6V6z"/></svg>';

/** Unsaved Transfers-page draft, stashed so navigating away (other games,
 *  app restart) never loses team edits — restored on the next WC mount. */
const DRAFT_STASH_KEY = 'princhess.wc.draftStash';

const SHIELD_L = 'M3.37752 5.08241C3 5.62028 3 7.21907 3 10.4167V11.9914C3 17.6294 7.23896 20.3655 9.89856 21.5273C10.62 21.8424 10.9807 22 12 22V2C11.1886 2 10.405 2.26824 8.83772 2.80472L8.26491 3.00079C5.25832 4.02996 3.75503 4.54454 3.37752 5.08241Z';
const SHIELD_R = 'M21 11.9914V10.4167C21 7.21907 21 5.62028 20.6225 5.08241C20.245 4.54454 18.7417 4.02996 15.7351 3.00079L15.1623 2.80472C13.595 2.26824 12.8114 2 12 2V22C13.0193 22 13.38 21.8424 14.1014 21.5273C16.761 20.3655 21 17.6294 21 11.9914Z';
function shieldSvg(primary: string, secondary: string, cls = ''): string {
  // Neutral grey outline (not black) so navy/black shields stay perceptible
  // against dark-mode backgrounds, while staying subtle on light ones.
  const edge = 'rgba(148, 153, 166, 0.9)';
  return `<svg class="fpl-shield ${cls}" viewBox="0 0 24 24" aria-hidden="true">`
    + `<path d="${SHIELD_L}" fill="${primary}"/><path d="${SHIELD_R}" fill="${secondary}"/>`
    + `<path d="${SHIELD_L}" fill="none" stroke="${edge}" stroke-width="0.6"/>`
    + `<path d="${SHIELD_R}" fill="none" stroke="${edge}" stroke-width="0.6"/></svg>`;
}

/** Match the FPL row class names so the per-row max-width + centering rules
 *  (`.fpl-row.gk`, `.fpl-row.mid` etc.) apply on the WC pitch too. */
const POS_ROW_CLASS: Record<WcPosId, string> = { GK: 'gk', DEF: 'def', MID: 'mid', FWD: 'fwd' };

/** ISO3 codes for teams whose api-football `code` is null — without this they
 *  render as a full country name (e.g. "Curaçao") where a 3-letter code is
 *  expected, breaking the tidy CODE column. Keyed by api-sports team id. */
const TEAM_CODE_OVERRIDE: Record<number, string> = { 5530: 'CUW' }; // Curaçao

/** api-football fixture statuses: a match in play vs one that has finished. */
const LIVE_STATUSES = new Set(['1H', 'HT', '2H', 'ET', 'BT', 'P', 'SUSP', 'INT', 'LIVE']);
const DONE_STATUSES = new Set(['FT', 'AET', 'PEN', 'WO', 'AWD']);

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

/** Up to two initials for the no-photo placeholder (first + last name token). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Dark or light initials, whichever reads on the given background — so white
 *  and yellow kits (England, Brazil, Sweden…) don't get illegible white text. */
function textOn(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '#fff';
  const n = parseInt(m[1], 16);
  const lum = (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum > 0.62 ? '#16181d' : '#fff';
}

interface Identity { name: string; primary: string; secondary: string; }

/**
 * World Cup team builder. Loads the player snapshot once (WcClient reads the
 * worker's stored ingest — never a live api-football call); selection is fully
 * in memory. A 15-man squad (2-5-5-3), valid XI, captain/vice, budget cap, max 3
 * per country. List + pitch views (pitch reuses the FPL pitch with circular
 * country flags), a Team switcher (your team / opponent, read-only), and a
 * League ladder that works before the tournament.
 */
export class WcScreen {
  private root: HTMLElement;
  private el: HTMLElement;
  private client: WcClient;
  private cb: WcScreenCallbacks;
  private youId: string;
  private opponentId: string;
  private squads: WcSquad[];
  private kitColors: Array<{ name: string; hex: string }>;
  private budgetCap: number;

  private byId = new Map<number, WcPlayer>();
  private teamById = new Map<number, WcTeam>();
  private players: WcPlayer[] = [];
  private draft: WcDraft = { starters: [], bench: [], captainId: null, viceCaptainId: null };
  private identity: Identity = { name: '', primary: DEFAULT_PRIMARY, secondary: DEFAULT_SECONDARY };
  private loaded = false;
  private tab: WcTab = 'team';
  private view: WcView = 'you';
  private mode: WcMode = 'pitch';
  /** Gameweek index shown by the Matches pager (null = follow the active GW). */
  private gwSel: number | null = null;
  /** Swap source on the Transfers page (null = not in swap mode). */
  private swapId: number | null = null;
  private filterPos: WcPosId | 'ALL' = 'ALL';
  private filterCountry = 0;
  private search = '';
  private modal: HTMLElement | null = null;
  private poolModal: HTMLElement | null = null;
  private live: WcLive | null = null;
  private liveTimer: ReturnType<typeof setInterval> | null = null;

  constructor(root: HTMLElement, client: WcClient, cb: WcScreenCallbacks, data: WcScreenData) {
    this.root = root;
    this.client = client;
    this.cb = cb;
    this.youId = data.youId;
    this.opponentId = data.opponentId;
    this.squads = data.squads;
    this.kitColors = data.kitColors ?? KIT_COLORS;
    this.budgetCap = data.budgetCap ?? WC_BUDGET_CAP;
    this.el = document.createElement('div');
    this.el.className = 'wc';
    this.root.appendChild(this.el);
    this.el.innerHTML = `<p class="wc-loading">Loading World Cup squads…</p>`;
    void this.load();
    this.cb.onRequestSquads();
  }

  destroy(): void { this.stopLiveTimer(); this.closeModal(); this.closePoolModal(); this.el.remove(); }

  private stopLiveTimer(): void { if (this.liveTimer != null) { clearInterval(this.liveTimer); this.liveTimer = null; } }

  /** Fetch live game state. Re-renders on tabs that show it — but never the
   *  Transfers tab, where a background refresh would clobber an in-flight draft. */
  private async refreshLive(rerender = true): Promise<void> {
    try {
      this.live = await this.client.getLive();
      if (rerender && this.tab !== 'transfers') this.renderBody();
    } catch { /* leave previous state; transient */ }
  }

  setSquads(squads: WcSquad[]): void {
    // Skip the re-render when nothing changed (e.g. the resync that follows a
    // rejected save) so an on-screen status message isn't wiped mid-read.
    const changed = JSON.stringify(squads) !== JSON.stringify(this.squads);
    this.squads = squads;
    if (this.loaded && changed) {
      // Adopt your saved squad only if you haven't started a fresh draft.
      if (this.draft.starters.length === 0 && this.draft.bench.length === 0) this.adoptSaved();
      this.renderBody();
    }
  }

  private async load(): Promise<void> {
    try {
      const [players, teams] = await Promise.all([this.client.getPlayers(), this.client.getTeams()]);
      this.players = players;
      this.byId = new Map(players.map((p) => [p.id, p]));
      this.teamById = new Map(teams.map((t) => [t.id, t]));
      this.loaded = true;
      this.adoptSaved();
      this.restoreDraftStash();
      this.renderShell();
      void this.refreshLive(); // powers deadlines, lock state, fixtures + scores
    } catch {
      this.el.innerHTML = `<p class="wc-loading">Couldn't load the player list. Try again shortly.</p>`;
    }
  }

  /** Keep unsaved edits across navigation: while the draft is dirty, mirror it
   *  to localStorage; once clean (saved or reverted), drop the stash. Called on
   *  every render so it can never drift from what's on screen. */
  private persistDraftStash(): void {
    try {
      if (this.isDirty()) {
        localStorage.setItem(DRAFT_STASH_KEY, JSON.stringify({ userId: this.youId, ts: Date.now(), draft: this.draft }));
      } else {
        localStorage.removeItem(DRAFT_STASH_KEY);
      }
    } catch { /* storage full/blocked — non-fatal */ }
  }

  /** Re-adopt a stashed unsaved draft (newer than the last save) and reopen the
   *  Transfers page so you come back exactly where you left off. */
  private restoreDraftStash(): void {
    try {
      const raw = localStorage.getItem(DRAFT_STASH_KEY);
      if (!raw) return;
      const stash = JSON.parse(raw) as { userId: string; ts: number; draft: WcDraft };
      const saved = this.savedFor(this.youId);
      if (stash.userId !== this.youId || stash.ts <= (saved?.submittedAt ?? 0)) {
        localStorage.removeItem(DRAFT_STASH_KEY);
        return;
      }
      const keep = (ids: number[]): number[] => (ids ?? []).filter((id) => this.byId.has(id));
      this.draft = {
        starters: keep(stash.draft.starters),
        bench: keep(stash.draft.bench),
        captainId: stash.draft.captainId != null && this.byId.has(stash.draft.captainId) ? stash.draft.captainId : null,
        viceCaptainId: stash.draft.viceCaptainId != null && this.byId.has(stash.draft.viceCaptainId) ? stash.draft.viceCaptainId : null,
      };
      if (this.isDirty()) this.tab = 'transfers';
      else localStorage.removeItem(DRAFT_STASH_KEY);
    } catch { /* corrupt stash → ignore */ }
  }

  private savedFor(userId: string): WcSquad | undefined {
    return this.squads.find((s) => s.userId === userId);
  }

  /** Normalise a stored squad into a draft (drops pool-pruned ids + -1 sentinels). */
  private draftFromSquad(saved: WcSquad): WcDraft {
    const keep = (ids: number[]): number[] => ids.filter((id) => this.byId.has(id));
    return {
      starters: keep(saved.picks),
      bench: keep(saved.bench),
      captainId: saved.captainId && this.byId.has(saved.captainId) ? saved.captainId : null,
      viceCaptainId: saved.viceCaptainId && this.byId.has(saved.viceCaptainId) ? saved.viceCaptainId : null,
    };
  }

  private adoptSaved(): void {
    const saved = this.savedFor(this.youId);
    if (!saved) return;
    this.draft = this.draftFromSquad(saved);
    this.identity = {
      name: saved.name ?? '',
      primary: saved.primaryColor ?? DEFAULT_PRIMARY,
      secondary: saved.secondaryColor ?? DEFAULT_SECONDARY,
    };
  }

  /** Draft differs from your last saved squad (XI/bench/captaincy). */
  private isDirty(): boolean {
    const saved = this.savedFor(this.youId);
    const ref: WcDraft = saved ? this.draftFromSquad(saved) : { starters: [], bench: [], captainId: null, viceCaptainId: null };
    return JSON.stringify(ref) !== JSON.stringify(this.draft);
  }

  private countryName(teamId: number): string {
    const t = this.teamById.get(teamId);
    return TEAM_CODE_OVERRIDE[teamId] || t?.code || t?.name || '—';
  }
  private flagUrl(teamId: number): string | null { return this.teamById.get(teamId)?.logo ?? null; }
  private all(): number[] { return [...this.draft.starters, ...this.draft.bench]; }

  /** Bench in fixed positional order — GK, then DEF, MID, FWD (stable within a
   *  position). Used for display and persisted on save so the bench always reads
   *  GK → defenders → midfielders → attackers. */
  private benchSorted(ids: number[]): number[] {
    return [...ids].sort((a, b) =>
      WC_POSITIONS.indexOf(this.byId.get(a)?.position ?? 'FWD') - WC_POSITIONS.indexOf(this.byId.get(b)?.position ?? 'FWD'));
  }

  /** Current total market value of a saved squad (sum of its 15 players' prices). */
  private squadValueOf(squad: WcSquad | undefined): number {
    if (!squad) return 0;
    return [...squad.picks, ...squad.bench].reduce((s, id) => s + (this.byId.get(id)?.budget ?? 0), 0);
  }

  /** Your usable budget = max(£100m, current value of the team you own). Owned
   *  players that rise lift it (value picks pay off); falling ones can't drop it
   *  below the £100m floor, so you're never trapped with too little to rebuild a
   *  full team. Equals the base cap until prices move (see WC_VALUATION.md L2). */
  private usableBudget(): number {
    return Math.max(this.budgetCap, this.squadValueOf(this.savedFor(this.youId)));
  }

  /** Max players per country for the round being built (relaxes in late KOs). */
  private countryLimit(): number {
    return wcCountryLimit(this.activeGw()?.gw.index);
  }

  /** Nations still in the tournament for the round being built (null = no
   *  restriction). Governs who can be transferred IN — eliminated countries'
   *  players are hidden from the pool entirely — but never touches players
   *  already in the squad, so a user can keep a knocked-out pick if they'd
   *  rather not spend a transfer replacing them. */
  private transferTeams(): Set<number> | null {
    return wcTransferTeams(this.activeGw()?.gw.index);
  }

  /** Circular player avatar: the api-sports headshot when present, otherwise a
   *  country-colour disc with the player's initials (the worker nulls `photo`
   *  when the source is the generic silhouette). `opts.inner` adds overlaid
   *  badges (captain/vice, remove); `opts.flag` overlays a small nationality
   *  chip (used on the pitch, where the country isn't shown as text). */
  private avatar(p: WcPlayer, opts: { sm?: boolean; lg?: boolean; flag?: boolean; inner?: string } = {}): string {
    const size = opts.sm ? ' sm' : opts.lg ? ' lg' : '';
    const f = opts.flag ? this.flagUrl(p.teamId) : null;
    const chip = f ? `<span class="wc-natflag" style="background-image:url('${f}')"></span>` : '';
    const extra = (opts.inner ?? '') + chip;
    if (p.photo) return `<span class="wc-flag photo${size}" style="background-image:url('${p.photo}')">${extra}</span>`;
    const bg = countryColor(p.teamId);
    return `<span class="wc-flag ph${size}" style="background-color:${bg};color:${textOn(bg)}"><span class="wc-initials">${esc(initials(p.name))}</span>${extra}</span>`;
  }

  // ─── shell + switchers ──────────────────────────────────────────────────────

  private renderShell(): void {
    this.el.innerHTML = `
      <div class="wc-tabs" data-wc-tabs></div>
      <div class="wc-body" data-wc-body></div>`;
    this.renderSwitchers();
    this.renderBody();
  }

  /** Top nav: Your Team / Fixtures / League. The opponent's team is reached by
   *  tapping their card (Fixtures) or their League row — not a dropdown. */
  private renderSwitchers(): void {
    const host = this.el.querySelector('[data-wc-tabs]') as HTMLElement | null;
    if (!host) return;
    const tabs: Array<{ t: WcTab; label: string }> = [
      { t: 'team', label: 'Your Team' }, { t: 'gw', label: 'Fixtures' }, { t: 'league', label: 'League' },
    ];
    // 'transfers' lives under the Your Team tab; viewing the opponent also sits
    // under it, but we don't light the tab so it's clear you've navigated away.
    const onTeam = (this.tab === 'team' || this.tab === 'transfers') && this.view === 'you';
    host.innerHTML = tabs.map((x) => {
      const active = x.t === 'team' ? onTeam : this.tab === x.t;
      return `<button class="wc-tab ${active ? 'active' : ''}" type="button" data-wc-tab="${x.t}">${x.label}</button>`;
    }).join('');
    host.querySelectorAll<HTMLButtonElement>('[data-wc-tab]').forEach((b) =>
      b.addEventListener('click', () => {
        const t = b.dataset.wcTab as WcTab;
        if (t === 'team') this.view = 'you'; // Your Team always returns to your own team
        this.setTab(t);
      }));
  }

  /** Switch the team view to a manager (own ↔ opponent), from a card/league tap. */
  private viewManager(userId: string): void {
    this.view = userId === this.opponentId ? 'opp' : 'you';
    this.tab = 'team';
    this.renderSwitchers();
    this.renderBody();
  }

  private setTab(tab: WcTab): void {
    this.tab = tab;
    if (tab !== 'gw') this.gwSel = null; // pager snaps back to the active GW
    this.swapId = null; // leaving a page always exits swap mode
    void this.refreshLive(); // freshen deadlines/scores (re-renders when it lands)
    this.renderSwitchers();
    this.renderBody();
  }

  private body(): HTMLElement { return this.el.querySelector('[data-wc-body]') as HTMLElement; }

  private renderBody(): void {
    if (!this.loaded) return;
    if (this.players.length === 0) {
      this.body().innerHTML = `<p class="wc-loading">The World Cup player list is still syncing — check back soon.</p>`;
      return;
    }
    // Auto-refresh live scores on the Matches view, and on the Team page while a
    // gameweek is in play (so points appear without a manual reload). Gated on a
    // live activeRound so it stops once the tournament is over (no endless poll).
    const wantTimer = this.tab === 'gw' || (this.tab === 'team' && this.live?.activeRound != null && this.inLiveGw());
    if (wantTimer) { if (this.liveTimer == null) this.liveTimer = setInterval(() => void this.refreshLive(), 45_000); }
    else this.stopLiveTimer();

    this.persistDraftStash(); // unsaved edits survive navigating anywhere
    if (this.tab === 'league') return this.renderLeague();
    if (this.tab === 'gw') return this.renderGw();
    if (this.tab === 'transfers') return this.renderTransfers();
    this.renderTeam();
  }

  // ─── Matches view (merged Live + Season, pageable per gameweek) ────────────

  /** The current gameweek from live state + its state relative to `now`. */
  private activeGw(): { gw: WcLive['gameweeks'][number]; state: 'upcoming' | 'live' | 'complete' } | null {
    const live = this.live;
    if (!live) return null;
    const gw = live.gameweeks.find((g) => !g.complete) ?? live.gameweeks[live.gameweeks.length - 1];
    if (!gw) return null;
    return { gw, state: this.gwState(gw) };
  }

  private gwState(gw: WcLive['gameweeks'][number]): 'upcoming' | 'live' | 'complete' {
    if (gw.complete) return 'complete';
    return Date.parse(this.live!.now) < Date.parse(gw.deadline) ? 'upcoming' : 'live';
  }

  /** Manager's score for one gameweek: settled (with auto-subs/hits) once the
   *  round finalises; provisional live total for the in-play round (starters'
   *  live pts + captain double); null for future rounds. */
  private managerGwScore(userId: string, gw: WcLive['gameweeks'][number]): { score: number; settled: boolean } | null {
    const live = this.live!;
    const mgr = live.managers.find((m) => m.userId === userId);
    const hist = mgr?.history?.[gw.round];
    if (hist && hist.points != null) return { score: hist.points, settled: true };
    if (gw.round !== live.activeRound || this.gwState(gw) === 'upcoming') return null;
    const lp = live.livePlayers;
    // Score the LOCKED starters for the in-play round (not the editable draft) so
    // a locked / merged mid-round shows the true locked team's live total. Fall
    // back to the saved draft only for unlocked late-joiners with no locked squad.
    const lockedPicks = mgr?.lockedPicks;
    if (lockedPicks && lockedPicks.length) {
      let score = lockedPicks.reduce((s, id) => s + (lp[id]?.points ?? 0), 0);
      const cap = mgr?.lockedCaptainId;
      if (cap && lockedPicks.includes(cap)) score += lp[cap]?.points ?? 0;
      return { score, settled: false };
    }
    const saved = this.savedFor(userId);
    if (!saved) return { score: 0, settled: false };
    let score = saved.picks.reduce((s, id) => s + (lp[id]?.points ?? 0), 0);
    if (saved.captainId && saved.picks.includes(saved.captainId)) score += lp[saved.captainId]?.points ?? 0;
    return { score, settled: false };
  }

  private deadlineLabel(iso: string): string {
    const ms = Date.parse(iso) - Date.now();
    if (ms <= 0) return 'now';
    const h = Math.floor(ms / 3.6e6), d = Math.floor(h / 24);
    if (d >= 1) return `${d}d ${h % 24}h`;
    const m = Math.floor(ms / 6e4);
    return h >= 1 ? `${h}h ${m % 60}m` : `${m}m`;
  }

  private teamNameFor(userId: string): string {
    return this.savedFor(userId)?.name?.trim() || this.defaultTeamName(userId);
  }

  private renderGw(): void {
    if (!this.live) { this.body().innerHTML = `<p class="wc-loading">Loading fixtures…</p>`; void this.refreshLive(); return; }
    const gws = this.live.gameweeks;
    if (!gws.length) { this.body().innerHTML = `<p class="wc-loading">Fixtures appear once the schedule is published.</p>`; return; }
    const activeIdx = (gws.find((g) => !g.complete) ?? gws[gws.length - 1]).index;
    const sel = Math.min(Math.max(this.gwSel ?? activeIdx, 1), gws.length);
    const gw = gws.find((g) => g.index === sel) ?? gws[gws.length - 1];
    const state = this.gwState(gw);
    const status = state === 'upcoming'
      ? `<span class="wc-gw-state">Deadline in ${this.deadlineLabel(gw.deadline)}</span>`
      : state === 'live' ? `<span class="wc-gw-state live">● LIVE</span>`
      : gw.final ? `<span class="wc-gw-state">Final</span>` : `<span class="wc-gw-state">Finishing up…</span>`;

    const you = this.managerGwScore(this.youId, gw), opp = this.managerGwScore(this.opponentId, gw);
    const card = (userId: string, s: { score: number; settled: boolean } | null, other: { score: number } | null): string => {
      const mgr = this.live!.managers.find((m) => m.userId === userId);
      const lead = s && (!other || s.score >= other.score) ? 'lead' : '';
      return `
      <div class="wc-h2h-card tappable ${lead}" data-wc-h2h="${esc(userId)}" role="button" tabindex="0">
        <div class="wc-h2h-name">${esc(this.teamNameFor(userId))}</div>
        <div class="wc-h2h-score">${s ? s.score : '—'}</div>
        <div class="wc-h2h-sub">TOTAL ${mgr?.totalPoints ?? 0}</div>
      </div>`;
    };

    const isActiveLive = gw.round === this.live.activeRound && state !== 'upcoming';
    this.body().innerHTML = `
      <div class="wc-gw-nav">
        <button class="wc-gw-arrow" type="button" data-wc-gw-prev ${sel <= 1 ? 'disabled' : ''} aria-label="Previous gameweek">‹</button>
        <div class="wc-gw-title"><h3>GW ${gw.index}</h3><span class="wc-gw-round">${esc(gw.round)}</span>${status}</div>
        <button class="wc-gw-arrow" type="button" data-wc-gw-next ${sel >= gws.length ? 'disabled' : ''} aria-label="Next gameweek">›</button>
      </div>
      <div class="wc-h2h">${card(this.youId, you, opp)}<span class="wc-h2h-v">v</span>${card(this.opponentId, opp, you)}</div>
      ${state === 'upcoming' ? `<p class="wc-gw-cutoff">Transfers lock ${this.fmtKickoff(gw.deadline)} — 5 min before kick-off.</p>` : ''}
      <div class="wc-fixtures">${this.renderFixtures(gw)}</div>
      ${isActiveLive ? `<div class="wc-live-team">${this.renderLiveTeam(this.youId)}</div>` : ''}
      <div class="wc-season-strip">Season&nbsp;·&nbsp;${esc(this.teamNameFor(this.youId))} <strong>${this.totalFor(this.youId)}</strong>
        <span class="wc-h2h-v">v</span> <strong>${this.totalFor(this.opponentId)}</strong> ${esc(this.teamNameFor(this.opponentId))}</div>`;
    this.body().querySelector('[data-wc-gw-prev]')?.addEventListener('click', () => { this.gwSel = sel - 1; this.renderBody(); });
    this.body().querySelector('[data-wc-gw-next]')?.addEventListener('click', () => { this.gwSel = sel + 1; this.renderBody(); });
    this.body().querySelectorAll<HTMLElement>('[data-wc-fx]').forEach((el) => {
      const open = (): void => this.openMatchModal(Number(el.dataset.wcFx));
      el.addEventListener('click', open);
      el.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } });
    });
    // Tap a manager's score card → jump to their team (own ↔ opponent).
    this.body().querySelectorAll<HTMLElement>('[data-wc-h2h]').forEach((el) => {
      const open = (): void => this.viewManager(el.dataset.wcH2h!);
      el.addEventListener('click', open);
      el.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } });
    });
  }

  private totalFor(userId: string): number {
    return this.live?.managers.find((m) => m.userId === userId)?.totalPoints ?? 0;
  }

  /** Fixture list for one gameweek, grouped by day. Unknown knockout pairings
   *  render as TBD placeholders until api-football fills the teams in. */
  /** A single Matches-style fixture row: teams + score/time + status chip.
   *  `tappable` adds the tap-to-open-summary affordance (Matches list only). */
  private fixtureRowHtml(f: WcLive['fixtures'][number], tappable = false): string {
    const teamCell = (teamId: number | null, homeSide: boolean): string => {
      if (teamId == null) return `<span class="wc-fx-team ${homeSide ? 'home' : 'away'}"><span class="wc-fx-tbd">TBD</span></span>`;
      const t = this.teamById.get(teamId);
      const flag = t?.logo ? `<span class="wc-fx-flag" style="background-image:url('${t.logo}')"></span>` : '';
      const name = `<span class="wc-fx-name">${esc(TEAM_CODE_OVERRIDE[teamId] || t?.code || t?.name || 'TBD')}</span>`;
      return `<span class="wc-fx-team ${homeSide ? 'home' : 'away'}">${homeSide ? name + flag : flag + name}</span>`;
    };
    const live = LIVE_STATUSES.has(f.status), done = DONE_STATUSES.has(f.status);
    const mid = live || done
      ? `<span class="wc-fx-score ${live ? 'live' : ''}">${f.homeGoals ?? 0}–${f.awayGoals ?? 0}</span>`
      : `<span class="wc-fx-time">${new Date(f.date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>`;
    const chip = live ? `<span class="wc-fx-chip live">LIVE</span>` : done ? `<span class="wc-fx-chip">${esc(f.status)}</span>` : '';
    const tap = tappable && f.homeTeamId != null && f.awayTeamId != null
      ? ` tappable" data-wc-fx="${f.id}" role="button" tabindex="0` : '';
    return `<div class="wc-fx${tap}">${teamCell(f.homeTeamId, true)}${mid}${teamCell(f.awayTeamId, false)}${chip}</div>`;
  }

  private renderFixtures(gw: WcLive['gameweeks'][number]): string {
    const fxs = this.live!.fixtures.filter((f) => f.round === gw.round)
      .sort((a, b) => (a.date < b.date ? -1 : 1));
    if (!fxs.length) return `<p class="wc-empty">No fixtures scheduled yet for this round.</p>`;
    const groups = new Map<string, typeof fxs>();
    for (const f of fxs) {
      const day = new Date(f.date).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
      if (!groups.has(day)) groups.set(day, []);
      groups.get(day)!.push(f);
    }
    let html = '';
    for (const [day, list] of groups) {
      html += `<div class="wc-fx-day">${esc(day)}</div>`;
      for (const f of list) html += this.fixtureRowHtml(f, true);
    }
    return html;
  }

  /** The player's fixture this round + its day label, as a Matches-style row —
   *  shown in the player modal so you can see their game and (once live) the
   *  running score. Empty when there's no known fixture for their team. */
  private playerFixtureHtml(playerId: number): string {
    const p = this.byId.get(playerId);
    const a = this.activeGw();
    if (!p || !a || !this.live) return '';
    const fx = this.live.fixtures.find((f) => f.round === a.gw.round
      && (f.homeTeamId === p.teamId || f.awayTeamId === p.teamId));
    if (!fx) return '';
    const day = new Date(fx.date).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
    return `<div class="wc-pm-fixture"><div class="wc-fx-day">${esc(day)}</div>${this.fixtureRowHtml(fx)}</div>`;
  }

  private fmtKickoff(iso: string): string {
    const d = new Date(iso);
    return `${d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })} ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
  }

  /** Your saved XI + bench with live gameweek points (captain doubled). */
  private renderLiveTeam(userId: string): string {
    const saved = this.savedFor(userId);
    if (!saved || saved.picks.length === 0) return `<p class="wc-empty">No team to score yet.</p>`;
    const lp = this.live!.livePlayers;
    const row = (id: number, starter: boolean): string => {
      const p = this.byId.get(id); if (!p) return '';
      const live = lp[id];
      const isC = id === saved.captainId, isV = id === saved.viceCaptainId;
      const badge = isC ? '<span class="wc-cv c">C</span>' : isV ? '<span class="wc-cv v">V</span>' : '';
      const playedDot = live && live.minutes > 0 ? '<span class="wc-played" title="played">●</span>' : '<span class="wc-played out" title="yet to play / DNP">○</span>';
      return `<div class="wc-pick${starter ? '' : ' sub'}">
        ${this.avatar(p, { sm: true })}
        <span class="wc-pos-tag pos-${p.position}">${p.position}</span>
        <span class="wc-pick-id"><span class="wc-pick-name">${esc(p.name)}</span>${badge}</span>
        ${playedDot}
        ${this.livePtsCell(id, saved.captainId, 'wc-pick-cost wc-live-pts')}</div>`;
    };
    return `<div class="wc-group"><h4>Starting XI</h4>${saved.picks.map((id) => row(id, true)).join('')}</div>`
      + `<div class="wc-group"><h4>Bench</h4>${saved.bench.map((id) => row(id, false)).join('')}</div>`;
  }

  // ─── team (read-only) + transfers (editable) ──────────────────────────────

  /** YOUR editability — per-manager when the server provides it (late joiners
   *  with no locked squad stay editable mid-round), else the global window. */
  private isLocked(): boolean {
    const live = this.live;
    if (!live) return false;
    const me = live.managers.find((m) => m.userId === this.youId);
    return me?.locked ?? !!live.locked;
  }

  private myFreeTransfers(): number {
    return this.live?.managers.find((m) => m.userId === this.youId)?.freeTransfers ?? 0;
  }

  /** Transfers made so far this window (current draft vs your last LOCKED squad)
   *  + the points hit they'd cost. Null while transfers are still unlimited
   *  (pre-GW1, or no locked squad to diff against). */
  /** Transfers free regardless of count — pre-GW1 (no locked squad to diff yet)
   *  or a knockout-reset round (server `unlimited` flag — the first knockout). */
  private transfersUnlimited(): boolean {
    return !this.live?.frozen || !!this.live?.unlimited;
  }

  /** Copy for the unlimited-transfers state, by reason. */
  private unlimitedReason(): string {
    return this.live?.frozen ? 'Unlimited transfers this knockout round' : 'Unlimited transfers until GW1';
  }

  private transferPreview(): { transfers: number; free: number; hits: number } | null {
    const live = this.live;
    if (!live || this.transfersUnlimited()) return null;
    const me = live.managers.find((m) => m.userId === this.youId);
    if (!me || !me.baseline) return null;
    const base = new Set(me.baseline);
    const transfers = this.all().filter((id) => !base.has(id)).length;
    const free = me.freeTransfers;
    return { transfers, free, hits: Math.max(0, transfers - free) * HIT_COST };
  }

  /** Deadline + free-transfers line (from live state). */
  private gwInfoLine(): string {
    const a = this.activeGw();
    if (!a || !this.live) return '';
    const dl = this.isLocked() ? `${esc(this.live.lockedRound ?? a.gw.round)} in play — team locked`
      : a.state === 'upcoming' ? `${esc(a.gw.round)} deadline in ${this.deadlineLabel(a.gw.deadline)}`
      : a.state === 'live' ? `${esc(a.gw.round)} — live` : `${esc(a.gw.round)} — final`;
    const ft = this.myFreeTransfers();
    const ftLabel = this.transfersUnlimited() ? this.unlimitedReason()
      : this.transferPreview() ? `${ft} free transfer${ft === 1 ? '' : 's'}` : 'Transfers free this week';
    return `<div class="wc-gw-info"><span>${dl}</span><span>${ftLabel}</span></div>`;
  }

  /** Gameweek header for the Team page: a pink "GW 03" pill, the round name in
   *  caps ("GROUP STAGE 3"), and a red ● LIVE flag while the round is in play.
   *  Replaces the old padlock banner — lock state already shows in gwInfoLine. */
  private gwHeaderHtml(rightHtml = ''): string {
    const a = this.activeGw();
    let left = '';
    if (a) {
      const pad = String(a.gw.index).padStart(2, '0');
      const live = a.state === 'live';
      // "Group Stage - 3" → "GROUP STAGE 3"; knockout names (Round of 16, Final) read fine as-is.
      const stage = a.gw.round.replace(/ - /g, ' ').toUpperCase();
      left = `<span class="wc-gw-badge">GW ${pad}</span>`
        + `<span class="wc-gw-stage">${esc(stage)}</span>`
        + (live ? '<span class="wc-gw-flag">● LIVE</span>' : '');
    }
    return `<div class="wc-gw-header">
        <div class="wc-gw-left">${left}</div>
        ${rightHtml}
      </div>`;
  }

  private defaultTeamName(userId: string = this.youId): string { return `${userId} FC`; }

  /** True once the active gameweek's deadline has passed (points are flowing). */
  private inLiveGw(): boolean {
    const a = this.activeGw();
    return !!a && a.state !== 'upcoming';
  }

  /** A player's points this gameweek as displayed (captain doubled). */
  private livePtsFor(id: number, captainId: number | null): number {
    const pts = this.live?.livePlayers?.[id]?.points ?? 0;
    return id === captainId ? pts * 2 : pts;
  }

  /** Where a player's club is in THIS gameweek's match: not started, in play, or
   *  finished. Drives the live-points display — hidden before kickoff, shown
   *  (0 if he didn't feature) once the match is live, inverted once it's done.
   *  'none' when no fixture is found (pre-schedule / unknown knockout pairing). */
  private playerMatchState(playerId: number): 'upcoming' | 'live' | 'done' | 'none' {
    const p = this.byId.get(playerId);
    const a = this.activeGw();
    if (!p || !a || !this.live) return 'none';
    const fx = this.live.fixtures.find((f) => f.round === a.gw.round
      && (f.homeTeamId === p.teamId || f.awayTeamId === p.teamId));
    if (!fx) return 'none';
    if (DONE_STATUSES.has(fx.status)) return 'done';
    if (LIVE_STATUSES.has(fx.status)) return 'live';
    return 'upcoming';
  }

  /** Live GW points chip for a player, styled by match state: a muted dash before
   *  kickoff (no total yet), the running total once the match is live (0 if he
   *  hasn't featured), and an inverted dark chip once the match is finished.
   *  `cls` is the base class for the surface (pitch card vs list row). */
  private livePtsCell(id: number, captainId: number | null, cls: string): string {
    const state = this.playerMatchState(id);
    // No total yet before kickoff — or when the player has no fixture this round
    // (e.g. an unknown knockout pairing), where a bare 0 would mislead.
    if (state === 'upcoming' || state === 'none') return `<span class="${cls} pre">–</span>`;
    return `<span class="${cls}${state === 'done' ? ' done' : ''}">${this.livePtsFor(id, captainId)}</span>`;
  }

  /** The header stat chip. During a gameweek the Team page shows your live GW
   *  points total; otherwise (and always on the editor) it shows the points
   *  that will be deducted for transfers, with free transfers beneath. */
  private headerChipHtml(mode: 'live' | 'transfers'): string {
    const a = this.activeGw();
    if (mode === 'live' && a && this.inLiveGw()) {
      const s = this.managerGwScore(this.youId, a.gw);
      return `<div class="wc-hits-chip compact" title="Your gameweek points">
          <span class="wc-hits-num">${s?.score ?? 0}<span class="wc-hits-unit">pts</span></span>
        </div>`;
    }
    const tp = this.transferPreview();
    const hits = tp?.hits ?? 0;
    const free = tp?.free ?? this.myFreeTransfers();
    // No baseline yet (pre-GW1, or a late joiner pre-first-lock) → transfers
    // are free regardless of the bank count.
    const ftSub = this.transfersUnlimited() ? (this.live?.frozen ? 'unlimited this round' : 'unlimited transfers')
      : tp ? `${free} free transfer${free === 1 ? '' : 's'}` : 'transfers free this week';
    return `<div class="wc-hits-chip" title="Points deducted for transfers at the deadline">
        <span class="wc-hits-num">${hits > 0 ? `−${hits}` : '0'}</span>
        <span class="wc-hits-sub">${esc(ftSub)}</span>
      </div>`;
  }

  /** Shared header for the Team and Transfers pages: tappable shield + name
   *  (dashed underline on the editor page to signal pressability), the stat
   *  chip on the right, and a one-line £spent / £budget readout. */
  private teamHeaderHtml(dashed: boolean, chip: 'live' | 'transfers'): string {
    const all = this.all();
    const cost = squadCost(all, this.byId);
    const budget = this.usableBudget();
    const name = this.identity.name.trim() || this.defaultTeamName();
    const idInner = `<span class="fpl-badge">${shieldSvg(this.identity.primary, this.identity.secondary)}</span>`
      + `<span class="wc-team-name${name.length > 20 ? ' long' : ''}">${esc(name)}</span>`;
    return `
      <div class="wc-team-head">
        <button class="wc-team-id tappable${dashed ? ' dashed' : ''}" type="button" data-wc-edit aria-label="Edit team name and badge">${idInner}</button>
        ${this.headerChipHtml(chip)}
      </div>
      <div class="wc-budget-line ${cost > budget + 1e-9 ? 'over' : ''}"><strong>${formatBudget(cost)}</strong> / ${formatBudget(budget)}</div>`;
  }

  /** Default page: your team, READ-ONLY. Tapping players pops their modal; all
   *  editing lives behind the Make Transfers button (disabled while locked). */
  private renderTeam(): void {
    if (this.view === 'opp') return this.renderOpponent();
    this.swapId = null; // swap mode lives on the Transfers page only
    const locked = this.isLocked();
    const hasTeam = this.all().length > 0;
    const pts = this.inLiveGw();
    const modeToggle = `<div class="wc-mode-toggle">
          <button class="wc-mode-pill ${this.mode === 'list' ? 'active' : ''}" type="button" data-wc-mode="list">List</button>
          <button class="wc-mode-pill ${this.mode === 'pitch' ? 'active' : ''}" type="button" data-wc-mode="pitch">Pitch</button>
        </div>`;
    this.body().innerHTML = `
      ${this.teamHeaderHtml(false, 'live')}
      ${this.gwHeaderHtml(modeToggle)}
      <div class="wc-view-area" data-wc-view-area></div>
      <div class="wc-controls">
        <button class="wc-save wc-make-transfers" type="button" data-wc-transfers ${locked ? 'disabled' : ''}>
          ${hasTeam ? 'Make transfers' : 'Pick your team'}</button>
      </div>`;
    const area = this.body().querySelector('[data-wc-view-area]') as HTMLElement;
    if (this.mode === 'pitch') this.renderPitch(area, this.draft, false, { pts });
    else { area.innerHTML = `<div class="wc-squad">${this.renderSquadList(this.draft, { pts })}</div>`; this.wireSquadListRows(false); }
    this.body().querySelector('[data-wc-edit]')?.addEventListener('click', () => this.openEditTeam());
    this.body().querySelectorAll<HTMLButtonElement>('[data-wc-mode]').forEach((b) =>
      b.addEventListener('click', () => { this.mode = b.dataset.wcMode as WcMode; this.renderBody(); }));
    this.body().querySelector('[data-wc-transfers]')?.addEventListener('click', () => this.setTab('transfers'));
  }

  /** The squad transfers are judged against: your last LOCKED squad once the
   *  game is live, else your last save (pre-GW1 nothing is ever charged). */
  private referenceIds(): number[] {
    const me = this.live?.managers.find((m) => m.userId === this.youId);
    if (this.live?.frozen && me?.baseline) return me.baseline.filter((id) => this.byId.has(id));
    const saved = this.savedFor(this.youId);
    return saved ? [...saved.picks, ...saved.bench].filter((id) => this.byId.has(id)) : [];
  }

  /** Players in the reference squad but not the current draft — i.e. the ones
   *  being transferred out. Restoring one reverses (and un-charges) that move. */
  private outIds(): number[] {
    const cur = new Set(this.all());
    return this.referenceIds().filter((id) => !cur.has(id));
  }

  /** Transfer count + free transfers + running points cost. The count is a pure
   *  set-diff of draft vs the last locked squad, so reversing a transfer
   *  (restore X, drop Y) automatically resets it — the server charges hits at
   *  the deadline with the same diff, so the preview can't disagree. */
  private transferPanelHtml(): string {
    if (this.transfersUnlimited()) return `<div class="wc-transfer-bar free">${esc(this.live?.frozen ? 'Unlimited transfers this knockout round — make as many as you like.' : 'Unlimited transfers until the GW1 deadline.')}</div>`;
    const tp = this.transferPreview();
    if (!tp) return `<div class="wc-transfer-bar free">No locked squad yet — transfers are free this week.</div>`;
    return `
      <div class="wc-transfer-panel">
        <div class="wc-tp-cell"><span class="wc-tp-num">${tp.transfers}</span><span class="wc-tp-label">transfer${tp.transfers === 1 ? '' : 's'}</span>
          <span class="wc-tp-sub">${tp.free} free transfer${tp.free === 1 ? '' : 's'}</span></div>
        <div class="wc-tp-cell"><span class="wc-tp-num ${tp.hits > 0 ? 'hit' : ''}">${tp.hits > 0 ? `−${tp.hits}` : '0'}</span><span class="wc-tp-label">points cost</span>
          <span class="wc-tp-sub">−${HIT_COST} pts per extra transfer</span></div>
      </div>`;
  }

  /** Tray of transferred-out / removed players, each with a Restore button (the
   *  empty pitch slot offers the pick-a-new-player path; this offers the undo). */
  private outTrayHtml(): string {
    const out = this.outIds();
    if (!out.length) return '';
    const rows = out.map((id) => {
      const p = this.byId.get(id)!;
      const block = this.addBlock(p);
      return `<div class="wc-pick out">
        ${this.avatar(p, { sm: true })}
        <span class="wc-pos-tag pos-${p.position}">${p.position}</span>
        <span class="wc-pick-id"><span class="wc-pick-name">${esc(p.name)}</span></span>
        <span class="wc-pick-country">${esc(this.countryName(p.teamId))}</span>
        <button class="wc-restore" type="button" data-wc-restore="${p.id}" ${block ? `disabled title="${esc(block)}"` : ''}>Restore</button></div>`;
    }).join('');
    return `<div class="wc-out-tray"><h4>${this.live?.frozen ? 'Transferred out' : 'Removed'} — restore to undo</h4>${rows}</div>`;
  }

  /** The team editor (the old Pick page): X's to remove, pool to add, captain/
   *  vice, auto-fill + save. Reached via Make Transfers; blocked while locked. */
  private renderTransfers(): void {
    if (this.view === 'opp') return this.renderOpponent();
    if (this.isLocked()) {
      this.body().innerHTML = `
        ${this.teamHeaderHtml(true, 'transfers')}
        <div class="wc-lock-banner">🔒 ${esc(this.live?.lockedRound ?? 'This gameweek')} is in play — transfers are locked until points are final.</div>
        <div class="wc-controls"><button class="wc-save" type="button" data-wc-back>Back to team</button></div>`;
      this.body().querySelector('[data-wc-back]')?.addEventListener('click', () => this.setTab('team'));
      // Identity edits (name/shield) stay allowed while squads are locked.
      this.body().querySelector('[data-wc-edit]')?.addEventListener('click', () => this.openEditTeam());
      return;
    }
    const valid = validateWcSquad(this.draft, this.byId, this.usableBudget(), this.countryLimit());
    // Saveable = something changed AND the squad is currently valid. Drives both
    // save buttons, so they're grey mid-edit (e.g. a player removed, slot empty)
    // and grey again the moment the saved state matches what's on screen.
    const saveable = valid.ok && this.isDirty();
    this.body().innerHTML = `
      ${this.teamHeaderHtml(true, 'transfers')}
      ${this.gwInfoLine()}
      ${this.transferPanelHtml()}
      ${this.outTrayHtml()}
      <div class="wc-pick-head">
        <p class="wc-status" data-wc-status>${this.swapId != null
          ? `Swapping <strong>${esc(this.byId.get(this.swapId)?.name ?? '')}</strong> — tap a highlighted player.`
          : esc(this.statusText(valid))}</p>
        ${this.swapId != null
          ? '<button class="wc-swap-cancel" type="button" data-wc-swap-cancel>Cancel swap</button>'
          : `<div class="wc-mode-toggle">
          <button class="wc-mode-pill ${this.mode === 'list' ? 'active' : ''}" type="button" data-wc-mode="list">List</button>
          <button class="wc-mode-pill ${this.mode === 'pitch' ? 'active' : ''}" type="button" data-wc-mode="pitch">Pitch</button>
        </div>`}
      </div>
      <div class="wc-view-area" data-wc-view-area></div>
      <button class="wc-add-row" type="button" data-wc-addplayer>${this.all().length >= WC_SQUAD_SIZE ? 'Browse player list' : '+ Add players'}</button>
      <div class="wc-controls">
        <button class="wc-auto" type="button" data-wc-auto>Auto-fill</button>
        <button class="wc-save" type="button" data-wc-save ${saveable ? '' : 'disabled'}>Save team</button>
      </div>
      <div class="wc-cancel-row"><button class="wc-cancel-link" type="button" data-wc-cancel ${this.isDirty() ? '' : 'disabled'}>Cancel — revert all changes</button></div>
      <button class="wc-fab-save${saveable ? ' dirty' : ''}" type="button" data-wc-fab aria-label="Save team" title="Save team" ${saveable ? '' : 'disabled'}>${SAVE_SVG}</button>
    `;
    const area = this.body().querySelector('[data-wc-view-area]') as HTMLElement;
    if (this.mode === 'pitch') this.renderPitch(area, this.draft, true);
    else { area.innerHTML = `<div class="wc-squad">${this.renderSquadList(this.draft)}</div>`; this.wireSquadListRows(true); }

    this.wirePickControls();
    this.body().querySelector('[data-wc-fab]')?.addEventListener('click', () => this.save());
    this.body().querySelector('[data-wc-swap-cancel]')?.addEventListener('click', () => this.cancelSwap());
    this.body().querySelector('[data-wc-addplayer]')?.addEventListener('click', () => this.openPoolModal('ALL'));
    this.body().querySelectorAll<HTMLButtonElement>('[data-wc-restore]').forEach((b) =>
      b.addEventListener('click', () => this.addPlayer(Number(b.dataset.wcRestore))));
    this.body().querySelector('[data-wc-cancel]')?.addEventListener('click', () => {
      if (this.savedFor(this.youId)) this.adoptSaved();
      else this.draft = { starters: [], bench: [], captainId: null, viceCaptainId: null }; // nothing ever saved → back to blank
      this.swapId = null;
      this.renderBody();
      this.flash('All changes reverted.');
    });
  }

  /** Friendly build status: no positional nagging — quotas are enforced
   *  silently by the add flow (XI rows cap, extras spill to the bench, full
   *  positions grey out in the pool). */
  private statusText(valid: ReturnType<typeof validateWcSquad>): string {
    if (valid.ok) return 'Team is valid — save it.';
    const n = this.all().length;
    if (n < WC_SQUAD_SIZE) return `Pick your 15-man squad — ${WC_SQUAD_SIZE - n} more to add.`;
    const r = valid.reason.toLowerCase();
    if (r.includes('captain') || r.includes('vice') || r.includes('budget')) return valid.reason;
    return 'Adjust your team to a legal starting XI.';
  }

  /** Tap a squad-list row → player modal (actions only when editable) — or, in
   *  swap mode, pick the swap target. */
  private wireSquadListRows(editable: boolean): void {
    this.body().querySelectorAll<HTMLElement>('[data-wc-pick]').forEach((rowEl) => {
      const open = (): void => {
        const id = Number(rowEl.dataset.wcPick);
        if (this.swapId != null) this.handleSwapTap(id);
        else this.openPlayerModal(id, editable);
      };
      rowEl.addEventListener('click', open);
      rowEl.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } });
    });
  }

  private renderOpponent(): void {
    const saved = this.savedFor(this.opponentId);
    const name = saved?.name?.trim() || this.defaultTeamName(this.opponentId);
    const primary = saved?.primaryColor ?? DEFAULT_PRIMARY;
    const secondary = saved?.secondaryColor ?? DEFAULT_SECONDARY;
    if (!saved || saved.picks.length === 0) {
      this.body().innerHTML = `
        <div class="wc-team-head"><div class="wc-team-id"><span class="fpl-badge">${shieldSvg(primary, secondary)}</span><span class="wc-team-name">${esc(name)}</span></div></div>
        <p class="wc-loading">${esc(name)} hasn't picked a team yet.</p>`;
      return;
    }
    const oppDraft: WcDraft = { starters: saved.picks.filter((id) => this.byId.has(id)), bench: saved.bench.filter((id) => this.byId.has(id)), captainId: saved.captainId, viceCaptainId: saved.viceCaptainId };
    const pts = this.inLiveGw();
    const a = this.activeGw();
    const oppChip = pts && a ? (() => {
      const s = this.managerGwScore(this.opponentId, a.gw);
      return `<div class="wc-hits-chip compact" title="${esc(name)}'s gameweek points">
          <span class="wc-hits-num">${s?.score ?? 0}<span class="wc-hits-unit">pts</span></span>
        </div>`;
    })() : '';
    this.body().innerHTML = `
      <div class="wc-team-head"><div class="wc-team-id"><span class="fpl-badge">${shieldSvg(primary, secondary)}</span><span class="wc-team-name${name.length > 20 ? ' long' : ''}">${esc(name)}</span></div>${oppChip}</div>
      <div class="wc-pick-head"><span class="wc-status">Viewing ${esc(name)}'s team</span>
        <div class="wc-mode-toggle">
          <button class="wc-mode-pill ${this.mode === 'list' ? 'active' : ''}" type="button" data-wc-mode="list">List</button>
          <button class="wc-mode-pill ${this.mode === 'pitch' ? 'active' : ''}" type="button" data-wc-mode="pitch">Pitch</button>
        </div></div>
      <div class="wc-view-area" data-wc-view-area></div>`;
    const area = this.body().querySelector('[data-wc-view-area]') as HTMLElement;
    if (this.mode === 'pitch') this.renderPitch(area, oppDraft, false, { pts });
    else { area.innerHTML = `<div class="wc-squad">${this.renderSquadList(oppDraft, { pts })}</div>`; this.wireSquadListRows(false); }
    this.body().querySelectorAll<HTMLButtonElement>('[data-wc-mode]').forEach((b) =>
      b.addEventListener('click', () => { this.mode = b.dataset.wcMode as WcMode; this.renderBody(); }));
  }

  private wirePickControls(): void {
    this.body().querySelector('[data-wc-edit]')?.addEventListener('click', () => this.openEditTeam());
    this.body().querySelectorAll<HTMLButtonElement>('[data-wc-mode]').forEach((b) =>
      b.addEventListener('click', () => { this.mode = b.dataset.wcMode as WcMode; this.renderBody(); }));
    this.body().querySelector('[data-wc-auto]')?.addEventListener('click', () => this.autoFill());
    this.body().querySelector('[data-wc-save]')?.addEventListener('click', () => this.save());
    this.wireSquadControls();
  }

  /** Captain/vice/remove on squad cards + pitch add-slots. Shared list+pitch. */
  private wireSquadControls(): void {
    this.body().querySelectorAll<HTMLButtonElement>('[data-wc-remove]').forEach((b) =>
      b.addEventListener('click', (e) => { e.stopPropagation(); this.removePlayer(Number(b.dataset.wcRemove)); }));
    this.body().querySelectorAll<HTMLButtonElement>('[data-wc-cap]').forEach((b) =>
      b.addEventListener('click', (e) => { e.stopPropagation(); this.setCaptain(Number(b.dataset.wcCap)); }));
    this.body().querySelectorAll<HTMLButtonElement>('[data-wc-vice]').forEach((b) =>
      b.addEventListener('click', (e) => { e.stopPropagation(); this.setVice(Number(b.dataset.wcVice)); }));
    // Empty slot → the add-player modal prefiltered to that position (never
    // switches the page view out from under you).
    this.body().querySelectorAll<HTMLElement>('[data-wc-addpos]').forEach((slot) =>
      slot.addEventListener('click', () => this.openPoolModal((slot.dataset.wcAddpos ?? 'ALL') as WcPosId | 'ALL')));
  }

  // ─── squad list + pitch renderers ─────────────────────────────────────────

  private cvBadge(id: number, draft: WcDraft): string {
    if (id === draft.captainId) return '<span class="fpl-cap c">C</span>';
    if (id === draft.viceCaptainId) return '<span class="fpl-cap v">V</span>';
    return '';
  }

  /** Swap-mode class for a card/row: source, eligible target, or dimmed. */
  private swapClass(id: number): string {
    if (this.swapId == null) return '';
    if (id === this.swapId) return ' swap-src';
    return this.swapTargets(this.swapId).has(id) ? ' swap-ok' : ' swap-dim';
  }

  private renderSquadList(draft: WcDraft, opts: { pts?: boolean } = {}): string {
    // Tapping a row opens the player modal (info always; captain/vice/remove only
    // on the Transfers page); the only inline marker is a small C/V circle after
    // the name. Keeps the row from overflowing narrow viewports — no inline
    // buttons to push content off-screen. During a gameweek the price column
    // becomes the player's GW points (captain doubled).
    const row = (id: number): string => {
      const p = this.byId.get(id)!;
      const badge = (id === draft.captainId) ? '<span class="wc-cv c">C</span>' : (id === draft.viceCaptainId) ? '<span class="wc-cv v">V</span>' : '';
      const tap = ` tappable${this.swapClass(id)}" data-wc-pick="${id}" role="button" tabindex="0`;
      const tail = opts.pts
        ? this.livePtsCell(id, draft.captainId, 'wc-pick-cost wc-live-pts')
        : `<span class="wc-pick-cost">${formatBudget(p.budget)}</span>`;
      return `<div class="wc-pick${tap}">
        ${this.avatar(p, { sm: true })}
        <span class="wc-pos-tag pos-${p.position}">${p.position}</span>
        <span class="wc-pick-id"><span class="wc-pick-name">${esc(p.name)}</span>${badge}</span>
        <span class="wc-pick-country">${esc(this.countryName(p.teamId))}</span>
        ${tail}</div>`;
    };
    const group = (label: string, ids: number[], ordered: boolean): string => {
      // Bench keeps its stored order — it's the auto-sub priority.
      const list = ordered ? [...ids].sort((a, b) => WC_POSITIONS.indexOf(this.byId.get(a)!.position) - WC_POSITIONS.indexOf(this.byId.get(b)!.position)) : ids;
      return `<div class="wc-group"><h4>${label}</h4>${list.map((id) => row(id)).join('') || '<p class="wc-empty">No players yet.</p>'}</div>`;
    };
    return group(`Starting XI (${draft.starters.length}/${WC_STARTERS})`, draft.starters, true)
      + group(`Bench (${draft.bench.length}/4)`, draft.bench, true);
  }

  /** Pitch view — reuses the FPL pitch layout (.fpl-pitch/.fpl-row/.fpl-pcard)
   *  with a circular country flag in place of the kit shirt. `opts.pts` adds a
   *  GW-points strip under each name (the Team page during a gameweek). */
  private renderPitch(host: HTMLElement, draft: WcDraft, editable: boolean, opts: { pts?: boolean } = {}): void {
    const starterCounts = positionCounts(draft.starters, this.byId);
    const displayed = this.displayedSlots(starterCounts);
    const card = (id: number): string => {
      const p = this.byId.get(id)!;
      // SVG cross (not the × glyph, which sits low in its line box) so the mark
      // is geometrically centred in the dot regardless of font metrics. Hidden
      // during swap mode — taps there pick swap targets, not removals.
      const rm = editable && this.swapId == null ? `<button type="button" class="fpl-remove-dot" data-wc-remove="${id}" aria-label="Remove">${CROSS_SVG}</button>` : '';
      const pts = opts.pts ? this.livePtsCell(id, draft.captainId, 'fpl-pcard-pts') : '';
      // Flag is a small rectangle pinned to the headshot's bottom-right, straddling
      // the nameplate; captain/vice + remove sit on the image (siblings of the
      // headshot, so they can protrude past the card edge without being clipped).
      const flag = this.flagUrl(p.teamId);
      const flagChip = flag ? `<span class="wc-pcard-flag" style="background-image:url('${flag}')"></span>` : '';
      const capClass = id === draft.captainId ? ' wc-cap-card' : '';
      return `<div class="fpl-pcard${this.swapClass(id)}${capClass}" data-pcard="${id}">
        <div class="wc-pcard-top">${this.avatar(p)}${flagChip}${this.cvBadge(id, draft)}${rm}</div>
        <div class="fpl-pcard-plate"><span class="fpl-pcard-name">${esc(p.name)}</span>${pts}</div>
      </div>`;
    };
    const empty = (pos: WcPosId | 'ALL'): string => `<div class="fpl-pcard empty" data-wc-addpos="${pos}"><div class="wc-pcard-top"><span class="wc-flag"></span></div><div class="fpl-pcard-plate"><span class="fpl-pcard-name dim">${pos === 'ALL' ? 'Add player' : `Add ${pos}`}</span></div></div>`;
    // Squad composition (2 GK / 5 DEF / 5 MID / 3 FWD) shown per row, top-right.
    const squadCounts = positionCounts([...draft.starters, ...draft.bench], this.byId);
    const rowHtml = (pos: WcPosId): string => {
      const members = draft.starters.filter((id) => this.byId.get(id)?.position === pos);
      const empties = editable ? Math.max(0, displayed[pos] - members.length) : 0;
      const tag = `<span class="wc-row-tag pos-${pos}">${pos} ${squadCounts[pos]}/${SQUAD_COMP[pos]}</span>`;
      return `<div class="fpl-row ${POS_ROW_CLASS[pos]}">${tag}${members.map(card).join('')}${Array.from({ length: empties }, () => empty(pos)).join('')}</div>`;
    };
    const benchCards = this.benchSorted(draft.bench).map(card).join('');
    const benchEmpties = editable ? Math.max(0, 4 - draft.bench.length) : 0;
    host.innerHTML = `
      <div class="wc-pitch-host">
        <div class="fpl-pitch f11 wc">
          ${POS_ORDER_HTML}
          ${WC_POSITIONS.map(rowHtml).join('')}
        </div>
        <div class="fpl-bench-row"><span class="fpl-bench-title">Bench</span><div class="fpl-bench-cards">${benchCards}${Array.from({ length: benchEmpties }, () => empty('ALL')).join('')}</div></div>
      </div>`;
    this.wireSquadControls();
    // Tap a player card → modal (info always; actions only when editable) — or,
    // in swap mode, pick the swap target. The remove-dot stops propagation.
    host.querySelectorAll<HTMLElement>('[data-pcard]').forEach((c) =>
      c.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('[data-wc-remove]') || (e.target as HTMLElement).closest('[data-wc-addpos]')) return;
        const id = Number(c.dataset.pcard);
        if (this.swapId != null) this.handleSwapTap(id);
        else this.openPlayerModal(id, editable);
      }));
  }

  /** Slots to show per position — default 1-4-4-2, growing with selection. */
  private displayedSlots(filled: Record<WcPosId, number>): Record<WcPosId, number> {
    const disp: Record<WcPosId, number> = { ...filled };
    let remaining = WC_STARTERS - (filled.GK + filled.DEF + filled.MID + filled.FWD);
    for (const pos of WC_POSITIONS) while (disp[pos] < DEFAULT_START[pos] && remaining > 0 && disp[pos] < FORMATION[pos].max) { disp[pos]++; remaining--; }
    for (const pos of ['DEF', 'MID', 'FWD', 'GK'] as WcPosId[]) while (remaining > 0 && disp[pos] < FORMATION[pos].max) { disp[pos]++; remaining--; }
    return disp;
  }

  // ─── pool ─────────────────────────────────────────────────────────────────

  private renderPool(): string {
    const posPills = (['ALL', ...WC_POSITIONS] as const)
      .map((pos) => `<button class="wc-pill ${this.filterPos === pos ? 'active' : ''}" type="button" data-wc-pos="${pos}">${pos}</button>`).join('');
    const teams = this.transferTeams();
    const countriesInPool = [...new Set(this.players.map((p) => p.teamId))]
      .filter((id) => teams === null || teams.has(id))
      .map((id) => this.teamById.get(id)).filter((t): t is WcTeam => !!t).sort((a, b) => a.name.localeCompare(b.name));
    const countryOpts = `<option value="0">All countries</option>` +
      countriesInPool.map((t) => `<option value="${t.id}" ${this.filterCountry === t.id ? 'selected' : ''}>${esc(t.name)}</option>`).join('');
    return `
      <div class="wc-pool-filters">
        <div class="wc-pills">${posPills}</div>
        <select class="wc-country" data-wc-country>${countryOpts}</select>
        <input class="wc-search" data-wc-search type="search" placeholder="Search player…" value="${esc(this.search)}" />
      </div>
      <div class="wc-pool-list" data-wc-pool-list>${this.renderPoolRows()}</div>`;
  }

  /** Pool rows with per-rule red hints: price red when over budget, country red
   *  at the 3-per-country cap, position tag red when that quota is full (several
   *  can apply at once). Blocked rows grey the add button to an empty circle. */
  private renderPoolRows(): string {
    const term = this.search.trim().toLowerCase();
    const teams = this.transferTeams();
    const rows = this.players
      .filter((p) => (teams === null || teams.has(p.teamId))
        && (this.filterPos === 'ALL' || p.position === this.filterPos)
        && (this.filterCountry === 0 || p.teamId === this.filterCountry)
        && (term === '' || p.name.toLowerCase().includes(term)))
      .sort((a, b) => b.budget - a.budget).slice(0, 120);
    if (rows.length === 0) return '<p class="wc-empty">No players match.</p>';
    return rows.map((p) => {
      const v = this.addVerdict(p);
      return `<div class="wc-pool-row${v.blocked ? ' blocked' : ''}">
        ${this.avatar(p, { sm: true })}
        <span class="wc-pos-tag pos-${p.position}${v.position ? ' bad' : ''}">${p.position}</span>
        <span class="wc-pool-name">${esc(p.name)}</span>
        <span class="wc-pool-country${v.country ? ' bad' : ''}">${esc(this.countryName(p.teamId))}</span>
        <span class="wc-pool-cost${v.budget ? ' bad' : ''}">${formatBudget(p.budget)}</span>
        <button class="wc-add${v.blocked ? ' off' : ''}" type="button" data-wc-add="${p.id}" ${v.blocked ? `disabled ${v.title ? `title="${esc(v.title)}"` : ''}` : ''}>${v.picked ? '✓' : v.blocked ? '' : '+'}</button></div>`;
    }).join('');
  }

  // ─── add-player modal (the pool) ──────────────────────────────────────────

  /** Open the player pool in a modal, optionally prefiltered to a position
   *  (tapping an empty GK slot opens it on GK). Stays open across adds so you
   *  can browse; the page behind re-renders live as the squad fills. */
  private openPoolModal(prefilter: WcPosId | 'ALL' = 'ALL'): void {
    this.closePoolModal();
    this.filterPos = prefilter;
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `
      <div class="modal wc-pool-modal" role="dialog" aria-modal="true" aria-label="Add player">
        <div class="wc-pm-head"><h2>Add player</h2><span class="wc-pm-count" data-wc-pm-count></span>
          <button class="wc-pm-close" type="button" data-wc-pm-close aria-label="Close">×</button></div>
        <div class="wc-pool" data-wc-pool-host></div>
      </div>`;
    document.body.appendChild(overlay);
    this.poolModal = overlay;
    this.renderPoolModalContent();
    overlay.addEventListener('click', (e) => { if (e.target === overlay) this.closePoolModal(); });
    overlay.querySelector('[data-wc-pm-close]')?.addEventListener('click', () => this.closePoolModal());
  }

  private closePoolModal(): void { if (this.poolModal) { this.poolModal.remove(); this.poolModal = null; } }

  private renderPoolModalContent(): void {
    const host = this.poolModal?.querySelector('[data-wc-pool-host]') as HTMLElement | null;
    if (!host) return;
    host.innerHTML = this.renderPool();
    this.updatePoolModalCount();
    host.querySelectorAll<HTMLButtonElement>('[data-wc-pos]').forEach((b) =>
      b.addEventListener('click', () => { this.filterPos = b.dataset.wcPos as WcPosId | 'ALL'; this.renderPoolModalContent(); }));
    const countrySel = host.querySelector('[data-wc-country]') as HTMLSelectElement | null;
    countrySel?.addEventListener('change', () => { this.filterCountry = Number(countrySel.value); this.renderPoolModalContent(); });
    const searchEl = host.querySelector('[data-wc-search]') as HTMLInputElement | null;
    searchEl?.addEventListener('input', () => { this.search = searchEl.value; this.refreshPoolModalRows(); });
    this.wirePoolModalAdds(host);
  }

  /** Swap just the rows (keeps the search input focused while typing and the
   *  scroll position steady when eligibility repaints after an add). */
  private refreshPoolModalRows(): void {
    const list = this.poolModal?.querySelector('[data-wc-pool-list]') as HTMLElement | null;
    if (!list) return;
    const scroll = list.scrollTop;
    list.innerHTML = this.renderPoolRows();
    list.scrollTop = scroll;
    this.wirePoolModalAdds(list);
  }

  private wirePoolModalAdds(root: HTMLElement): void {
    root.querySelectorAll<HTMLButtonElement>('[data-wc-add]').forEach((b) =>
      b.addEventListener('click', () => {
        this.addPlayer(Number(b.dataset.wcAdd)); // re-renders the page behind
        this.refreshPoolModalRows(); // eligibility shifts (budget/quotas) → repaint rows
        this.updatePoolModalCount();
      }));
  }

  private updatePoolModalCount(): void {
    const el = this.poolModal?.querySelector('[data-wc-pm-count]') as HTMLElement | null;
    if (!el) return;
    const remaining = this.usableBudget() - squadCost(this.all(), this.byId);
    el.textContent = `${this.all().length}/${WC_SQUAD_SIZE} · ${formatBudget(Math.max(0, remaining))} left`;
  }

  // ─── selection logic ──────────────────────────────────────────────────────

  /** Why a pool player can('t) be added, broken out per rule so the row can
   *  paint the offending fact red (price/country/position) instead of words. */
  private addVerdict(p: WcPlayer): { blocked: boolean; picked: boolean; position: boolean; country: boolean; budget: boolean; title: string | null } {
    const all = this.all();
    const picked = all.includes(p.id);
    const position = !picked && positionCounts(all, this.byId)[p.position] >= SQUAD_COMP[p.position];
    const country = !picked && (countryCounts(all, this.byId).get(p.teamId) ?? 0) >= this.countryLimit();
    const budget = !picked && squadCost(all, this.byId) + p.budget > this.usableBudget() + 1e-9;
    const reasons = [
      picked ? 'Already in your squad' : null,
      position ? `No ${POS_LABEL[p.position].toLowerCase()} slots left` : null,
      country ? `Max ${this.countryLimit()} per country` : null,
      budget ? 'Over budget' : null,
    ].filter((r): r is string => !!r);
    return { blocked: reasons.length > 0, picked, position, country, budget, title: reasons.join(' · ') || null };
  }

  private addBlock(p: WcPlayer): string | null { return this.addVerdict(p).title; }

  private addPlayer(id: number): void {
    const p = this.byId.get(id);
    if (!p || this.addBlock(p)) return;
    const startCounts = positionCounts(this.draft.starters, this.byId);
    if (this.draft.starters.length < WC_STARTERS && startCounts[p.position] < FORMATION[p.position].max) this.draft.starters.push(id);
    else this.draft.bench.push(id);
    if (this.draft.captainId == null && this.draft.starters.includes(id)) this.draft.captainId = id;
    else if (this.draft.viceCaptainId == null && this.draft.starters.includes(id) && id !== this.draft.captainId) this.draft.viceCaptainId = id;
    this.renderBody();
  }

  private removePlayer(id: number): void {
    this.draft.starters = this.draft.starters.filter((x) => x !== id);
    this.draft.bench = this.draft.bench.filter((x) => x !== id);
    if (this.draft.captainId === id) this.draft.captainId = null;
    if (this.draft.viceCaptainId === id) this.draft.viceCaptainId = null;
    this.renderBody();
  }

  private setCaptain(id: number): void {
    if (!this.draft.starters.includes(id)) return;
    if (this.draft.viceCaptainId === id) this.draft.viceCaptainId = this.draft.captainId;
    this.draft.captainId = id;
    this.renderBody();
  }

  private setVice(id: number): void {
    if (!this.draft.starters.includes(id)) return;
    if (this.draft.captainId === id) this.draft.captainId = this.draft.viceCaptainId;
    this.draft.viceCaptainId = id;
    this.renderBody();
  }

  // ─── swaps (XI ↔ bench, FPL rules) ────────────────────────────────────────

  /** Would the XI still be a legal formation if `outId` were replaced by `inId`? */
  private xiLegalAfterSwap(outId: number, inId: number): boolean {
    const xi = this.draft.starters.map((id) => (id === outId ? inId : id));
    const c = positionCounts(xi, this.byId);
    return WC_POSITIONS.every((p) => c[p] >= FORMATION[p].min && c[p] <= FORMATION[p].max);
  }

  /** Who the swap source may swap with, under FPL rules:
   *  - a starting outfielder ↔ any bench outfielder that keeps the XI legal;
   *  - a bench outfielder ↔ eligible starters, OR another bench outfielder
   *    (reordering bench priority);
   *  - GKs only ever swap with each other (never outfield, never bench order). */
  private swapTargets(srcId: number): Set<number> {
    const out = new Set<number>();
    const src = this.byId.get(srcId);
    if (!src) return out;
    const { starters, bench } = this.draft;
    const posOf = (id: number): WcPosId | undefined => this.byId.get(id)?.position;
    if (starters.includes(srcId)) {
      for (const b of bench) {
        if (src.position === 'GK' || posOf(b) === 'GK') {
          if (src.position === 'GK' && posOf(b) === 'GK') out.add(b);
        } else if (this.xiLegalAfterSwap(srcId, b)) out.add(b);
      }
    } else if (bench.includes(srcId)) {
      if (src.position === 'GK') {
        const startGk = starters.find((id) => posOf(id) === 'GK');
        if (startGk) out.add(startGk);
      } else {
        for (const x of starters) {
          if (posOf(x) === 'GK') continue;
          if (this.xiLegalAfterSwap(x, srcId)) out.add(x);
        }
        for (const b of bench) {
          if (b === srcId || posOf(b) === 'GK') continue;
          out.add(b); // bench order swap
        }
      }
    }
    return out;
  }

  private startSwap(id: number): void {
    this.swapId = id;
    this.renderBody();
  }

  private cancelSwap(): void {
    this.swapId = null;
    this.renderBody();
  }

  /** Keep armbands on the pitch through a swap: a benched captain/vice hands
   *  the band to the player coming in; never both bands on one player. */
  private fixArmbands(outgoing: number, incoming: number): void {
    const d = this.draft;
    if (d.captainId === outgoing) d.captainId = incoming;
    if (d.viceCaptainId === outgoing) d.viceCaptainId = incoming;
    if (d.captainId != null && d.captainId === d.viceCaptainId) d.viceCaptainId = null;
  }

  private doSwap(srcId: number, tgtId: number): void {
    const { starters, bench } = this.draft;
    const si = starters.indexOf(srcId), ti = starters.indexOf(tgtId);
    const sbi = bench.indexOf(srcId), tbi = bench.indexOf(tgtId);
    if (si >= 0 && tbi >= 0) { starters[si] = tgtId; bench[tbi] = srcId; this.fixArmbands(srcId, tgtId); }
    else if (sbi >= 0 && ti >= 0) { starters[ti] = srcId; bench[sbi] = tgtId; this.fixArmbands(tgtId, srcId); }
    else if (sbi >= 0 && tbi >= 0) { bench[sbi] = tgtId; bench[tbi] = srcId; } // bench priority order
    this.swapId = null;
    this.renderBody();
  }

  /** Tap routing while swap mode is active. */
  private handleSwapTap(id: number): void {
    if (this.swapId == null) return;
    if (id === this.swapId) { this.cancelSwap(); return; }
    if (this.swapTargets(this.swapId).has(id)) this.doSwap(this.swapId, id);
  }

  private autoFill(): void {
    const chosen: number[] = [];
    const countryUse = new Map<number, number>();
    const teams = this.transferTeams();
    for (const pos of WC_POSITIONS) {
      const pool = this.players
        .filter((p) => p.position === pos && (teams === null || teams.has(p.teamId)))
        .sort((a, b) => a.budget - b.budget);
      let picked = 0;
      for (const p of pool) {
        if (picked >= SQUAD_COMP[pos]) break;
        if ((countryUse.get(p.teamId) ?? 0) >= this.countryLimit()) continue;
        chosen.push(p.id); countryUse.set(p.teamId, (countryUse.get(p.teamId) ?? 0) + 1); picked++;
      }
      if (picked < SQUAD_COMP[pos]) { this.flash(`Not enough ${POS_LABEL[pos].toLowerCase()}s to auto-fill.`); return; }
    }
    if (squadCost(chosen, this.byId) > this.usableBudget() + 1e-9) { this.flash('Cheapest valid squad is still over budget.'); return; }
    const starters: number[] = []; const bench: number[] = [];
    for (const pos of WC_POSITIONS) {
      const ids = chosen.filter((id) => this.byId.get(id)!.position === pos);
      starters.push(...ids.slice(0, DEFAULT_START[pos])); bench.push(...ids.slice(DEFAULT_START[pos]));
    }
    const byBudgetDesc = [...starters].sort((a, b) => this.byId.get(b)!.budget - this.byId.get(a)!.budget);
    this.draft = { starters, bench, captainId: byBudgetDesc[0] ?? null, viceCaptainId: byBudgetDesc[1] ?? null };
    this.swapId = null;
    this.renderBody();
  }

  private squadFromDraft(): WcSquad {
    return {
      userId: this.youId, picks: this.draft.starters, bench: this.benchSorted(this.draft.bench),
      captainId: this.draft.captainId ?? -1, viceCaptainId: this.draft.viceCaptainId ?? -1,
      submittedAt: Date.now(),
      name: this.identity.name.trim() || undefined, primaryColor: this.identity.primary, secondaryColor: this.identity.secondary,
    };
  }

  private save(): void {
    const v = validateWcSquad(this.draft, this.byId, this.usableBudget(), this.countryLimit());
    if (!v.ok) { this.flash(v.reason); return; }
    if (this.isLocked()) { this.flash('Transfers are locked while the gameweek is in play.'); return; }
    const tp = this.transferPreview(); // summarise BEFORE the broadcast refreshes state
    const squad = this.squadFromDraft();
    this.cb.onSave(squad);
    // Optimistically adopt the save locally so the save buttons grey out NOW
    // (the server broadcast confirms a beat later); the stash clears with it.
    this.squads = [...this.squads.filter((s) => s.userId !== this.youId), squad];
    try { localStorage.removeItem(DRAFT_STASH_KEY); } catch { /* non-fatal */ }
    this.renderBody();
    this.showSaveConfirm(tp);
  }

  /** Post-save confirmation modal with the transfer consequences spelled out. */
  private showSaveConfirm(tp: { transfers: number; free: number; hits: number } | null): void {
    this.closeModal();
    const sub = this.transfersUnlimited()
      ? (this.live?.frozen ? 'Unlimited transfers this knockout round.' : 'Unlimited changes until the GW1 deadline.')
      : tp && tp.transfers > 0
        ? (tp.hits > 0
          ? `${tp.transfers} transfer${tp.transfers === 1 ? '' : 's'} — a −${tp.hits} pt hit applies at the deadline.`
          : `${tp.transfers} transfer${tp.transfers === 1 ? '' : 's'}, covered by free transfers — no points hit.`)
        : 'No transfers pending — your team is set for the deadline.';
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `
      <div class="modal wc-saved-modal" role="dialog" aria-modal="true" aria-label="Team saved">
        <div class="wc-saved-check">✓</div>
        <h2>Team saved</h2>
        <p class="wc-pm-meta">${esc(sub)}</p>
        <button class="primary" type="button" data-act="close">OK</button>
      </div>`;
    document.body.appendChild(overlay);
    this.modal = overlay;
    overlay.querySelector('[data-act="close"]')?.addEventListener('click', () => this.closeModal());
    overlay.addEventListener('click', (e) => { if (e.target === overlay) this.closeModal(); });
  }

  /** Server refused the save (deadline lock). Revert the draft to the stored
   *  squad, re-render (which now shows the lock state), and surface the reason. */
  onSaveRejected(reason: string): void {
    this.adoptSaved();
    this.swapId = null;
    void this.refreshLive(false); // pick up the lock state we evidently missed
    this.renderBody();
    this.flash(reason);
  }

  private flash(msg: string): void {
    const el = this.body().querySelector('[data-wc-status]') as HTMLElement | null;
    if (el) el.textContent = msg;
  }

  // ─── edit team (name + shield colours) ────────────────────────────────────

  private openEditTeam(): void {
    // Prefill the default name so an untouched save persists "[player] FC".
    if (!this.identity.name.trim()) this.identity.name = this.defaultTeamName();
    const swatches = (which: 'primary' | 'secondary'): string =>
      this.kitColors.map((c) => {
        const on = (which === 'primary' ? this.identity.primary : this.identity.secondary) === c.hex;
        return `<button type="button" class="fpl-swatch ${on ? 'on' : ''}" data-swatch="${which}" data-hex="${c.hex}" style="background:${c.hex}" aria-label="${c.name}"></button>`;
      }).join('');
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `
      <div class="modal" role="dialog" aria-modal="true" aria-label="Edit team">
        <h2>Edit team</h2>
        <div class="fpl-identity-top"><span class="fpl-badge" data-badge>${shieldSvg(this.identity.primary, this.identity.secondary)}</span>
          <input class="fpl-name-input" type="text" maxlength="40" placeholder="Name your team" data-name value="${esc(this.identity.name)}" aria-label="Team name" /></div>
        <div class="fpl-colors">
          <div class="fpl-color-row"><span class="fpl-color-label">Primary</span><div class="fpl-swatches">${swatches('primary')}</div></div>
          <div class="fpl-color-row"><span class="fpl-color-label">Secondary</span><div class="fpl-swatches">${swatches('secondary')}</div></div>
        </div>
        <button class="primary" type="button" data-save-team>Save</button>
      </div>`;
    document.body.appendChild(overlay);
    this.modal = overlay;
    const nameInput = overlay.querySelector('[data-name]') as HTMLInputElement;
    nameInput.addEventListener('input', () => { this.identity.name = nameInput.value; });
    overlay.querySelectorAll<HTMLButtonElement>('[data-swatch]').forEach((b) =>
      b.addEventListener('click', () => {
        const hex = b.dataset.hex!;
        if (b.dataset.swatch === 'primary') this.identity.primary = hex; else this.identity.secondary = hex;
        b.parentElement!.querySelectorAll<HTMLElement>('[data-swatch]').forEach((x) => x.classList.toggle('on', x === b));
        (overlay.querySelector('[data-badge]') as HTMLElement).innerHTML = shieldSvg(this.identity.primary, this.identity.secondary);
      }));
    (overlay.querySelector('[data-save-team]') as HTMLButtonElement).addEventListener('click', () => {
      this.closeModal();
      // Persist identity now (even if the squad isn't complete) so the opponent +
      // the League ladder see the team name/shield before the tournament.
      this.cb.onSave(this.squadFromDraft());
      this.renderSwitchers();
      this.renderBody();
    });
    overlay.addEventListener('click', (e) => { if (e.target === overlay) this.closeModal(); });
  }

  private closeModal(): void { if (this.modal) { this.modal.remove(); this.modal = null; } }

  /** The player's itemised gameweek scoring (Appearance/Goal/… → ±pts) for the
   *  modal. Falls back to a points/minutes line for rows scored before the
   *  breakdown existed, and to "yet to play" during a live round. */
  private playerBreakdownHtml(id: number): string {
    const lp = this.live?.livePlayers?.[id];
    if (lp?.detail?.length) {
      const lines = lp.detail.map((l) =>
        `<div class="wc-pm-line"><span>${esc(l.label)}</span><span class="${l.pts < 0 ? 'neg' : 'pos'}">${l.pts > 0 ? '+' : ''}${l.pts}</span></div>`).join('');
      return `<div class="wc-pm-lines">${lines}
        <div class="wc-pm-line total"><span>Total</span><span>${lp.points}</span></div></div>`;
    }
    if (lp && lp.minutes > 0) return `<p class="wc-pm-gw">This GW: <strong>${lp.points} pts</strong> · ${lp.minutes} min</p>`;
    // No scoring lines. If his club's match is live/finished he simply didn't
    // feature (a clear 0); otherwise his game is still to come.
    const state = this.playerMatchState(id);
    if (state === 'live' || state === 'done') {
      return `<div class="wc-pm-lines"><div class="wc-pm-line"><span>Did not play (0′)</span><span>0</span></div>`
        + `<div class="wc-pm-line total"><span>Total</span><span>0</span></div></div>`;
    }
    // Upcoming: the fixture row above already shows the game, so add nothing here.
    return '';
  }

  /** Tap-on-fixture modal: scoreline + goalscorers (assists summarised beneath)
   *  and, once the match is finished, the bonus-point winners. Stats come from
   *  the active round's livePlayers, so other-round fixtures show the score only. */
  private openMatchModal(fixtureId: number): void {
    const fx = this.live?.fixtures.find((f) => f.id === fixtureId);
    if (!fx) return;
    const teamLabel = (id: number | null): string => {
      if (id == null) return 'TBD';
      const t = this.teamById.get(id);
      return t?.name || TEAM_CODE_OVERRIDE[id] || t?.code || 'TBD';
    };
    const state = DONE_STATUSES.has(fx.status) ? 'done' : LIVE_STATUSES.has(fx.status) ? 'live' : 'upcoming';
    const isActiveRound = fx.round === this.live!.activeRound;
    const lp = this.live!.livePlayers;
    // Players in YOUR squad get an accent dot next to their name in the summary.
    const mine = new Set([...(this.savedFor(this.youId)?.picks ?? []), ...(this.savedFor(this.youId)?.bench ?? [])]);
    type Ev = { id: number; name: string; suffix: string; n: number };
    const eventsFor = (teamId: number | null): { goals: Ev[]; assists: Ev[]; bonus: Ev[] } => {
      const goals: Ev[] = [], assists: Ev[] = [], bonus: Ev[] = [];
      if (teamId != null && isActiveRound && state !== 'upcoming') {
        for (const p of this.players) {
          if (p.teamId !== teamId) continue;
          for (const l of lp[p.id]?.detail ?? []) {
            if (l.key === 'goals') goals.push({ id: p.id, name: p.name, suffix: l.count > 1 ? `×${l.count}` : '', n: l.count });
            else if (l.key === 'assists') assists.push({ id: p.id, name: p.name, suffix: l.count > 1 ? `×${l.count}` : '', n: l.count });
            else if (l.key === 'bonus') bonus.push({ id: p.id, name: p.name, suffix: `+${l.pts}`, n: l.pts });
          }
        }
        bonus.sort((a, b) => b.n - a.n);
      }
      return { goals, assists, bonus };
    };
    // Pressable name (→ read-only player modal) with optional 'in your team' dot.
    const chip = (e: Ev): string => {
      const dot = mine.has(e.id) ? '<sup class="wc-mm-mine" title="In your team">●</sup>' : '';
      return `<button type="button" class="wc-mm-name" data-wc-mm-player="${e.id}"><span class="wc-mm-nm">${esc(e.name)}</span>${e.suffix ? `<span class="wc-mm-sfx">${e.suffix}</span>` : ''}${dot}</button>`;
    };
    const cat = (label: string, items: Ev[]): string =>
      items.length ? `<div class="wc-mm-cat">${label}</div>${items.map((e) => `<div class="wc-mm-ev">${chip(e)}</div>`).join('')}` : '';
    const col = (teamId: number | null): string => {
      const ev = eventsFor(teamId);
      const teamGoals = (teamId === fx.homeTeamId ? fx.homeGoals : fx.awayGoals) ?? 0;
      const listed = ev.goals.reduce((s, e) => s + e.n, 0);
      let goalsSec = cat('Goals', ev.goals);
      // Non-zero scoreline with no listed scorers = detail still syncing; fewer
      // listed than scored = an own goal / non-pool scorer (never itemised).
      if (!ev.goals.length && teamGoals > 0) goalsSec = `<div class="wc-mm-cat">Goals</div><p class="wc-mm-note">loading…</p>`;
      else if (listed < teamGoals) goalsSec += `<p class="wc-mm-note">+${teamGoals - listed} other</p>`;
      const inner = (goalsSec + cat('Assists', ev.assists) + (state === 'done' ? cat('Bonus', ev.bonus) : '')) || '<p class="wc-mm-note">—</p>';
      return `<div class="wc-mm-col"><div class="wc-mm-colhead">${esc(teamLabel(teamId))}</div>${inner}</div>`;
    };
    let body: string;
    if (state === 'upcoming') body = `<p class="wc-mm-empty">Goalscorers appear once the match kicks off.</p>`;
    else if (!isActiveRound) body = `<p class="wc-mm-empty">Goalscorer detail is only shown for the live round.</p>`;
    else body = `<div class="wc-mm-cols">${col(fx.homeTeamId)}${col(fx.awayTeamId)}</div>`;
    const mid = state === 'upcoming'
      ? `<span class="wc-mm-score sm">${esc(this.fmtKickoff(fx.date))}</span>`
      : `<span class="wc-mm-score">${fx.homeGoals ?? 0}–${fx.awayGoals ?? 0}</span>`;
    const statusLabel = state === 'live' ? '● LIVE' : state === 'done' ? (fx.status === 'FT' ? 'Full time' : esc(fx.status)) : 'Upcoming';
    this.closeModal();
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `
      <div class="modal wc-match-modal" role="dialog" aria-modal="true" aria-label="Match summary">
        <div class="wc-mm-head"><span class="wc-mm-team home">${esc(teamLabel(fx.homeTeamId))}</span>${mid}<span class="wc-mm-team away">${esc(teamLabel(fx.awayTeamId))}</span></div>
        <div class="wc-mm-status ${state}">${statusLabel}</div>
        ${body}
        <button class="secondary" type="button" data-act="close">Close</button>
      </div>`;
    document.body.appendChild(overlay);
    this.modal = overlay;
    // Tap a name → read-only player modal (info + points, no team-edit actions).
    overlay.querySelectorAll<HTMLElement>('[data-wc-mm-player]').forEach((el) =>
      el.addEventListener('click', () => { const pid = Number(el.dataset.wcMmPlayer); this.closeModal(); this.openPlayerModal(pid, false); }));
    overlay.querySelector('[data-act="close"]')?.addEventListener('click', () => this.closeModal());
    overlay.addEventListener('click', (e) => { if (e.target === overlay) this.closeModal(); });
  }

  /** Tap-on-player modal: name + country + position + price + this gameweek's
   *  itemised points. Captain/vice/swap/remove actions only when editable. */
  private openPlayerModal(id: number, editable = true): void {
    const p = this.byId.get(id); if (!p) return;
    const isStarter = this.draft.starters.includes(id);
    const inSquad = isStarter || this.draft.bench.includes(id);
    const isC = this.draft.captainId === id;
    const isV = this.draft.viceCaptainId === id;
    const actions = editable ? `
          <button class="primary" type="button" data-act="swap" ${!inSquad ? 'disabled' : ''}>Swap</button>
          <button class="secondary" type="button" data-act="cap" ${!isStarter || isC ? 'disabled' : ''}>${isC ? 'Captain ✓' : 'Make captain'}</button>
          <button class="secondary" type="button" data-act="vice" ${!isStarter || isV ? 'disabled' : ''}>${isV ? 'Vice ✓' : 'Make vice'}</button>
          <button class="secondary" type="button" data-act="remove">Remove</button>
          <button class="secondary" type="button" data-act="close">Close</button>`
      : `<button class="secondary" type="button" data-act="close">Close</button>`;
    const t = this.teamById.get(p.teamId);
    const countryFull = (t?.name || t?.code || '—').toUpperCase();
    const flagLogo = t?.logo ? `<span class="wc-pm-flag" style="background-image:url('${t.logo}')"></span>` : '';
    const photo = p.photo
      ? `<div class="wc-pm-photo photo" style="background-image:url('${p.photo}')"></div>`
      : `<div class="wc-pm-photo ph"><span class="wc-pm-initials">${esc(initials(p.name))}</span></div>`;
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `
      <div class="modal wc-player-modal" role="dialog" aria-modal="true" aria-label="${esc(p.name)}">
        <div class="wc-pm-top">
          ${photo}
          <div class="wc-pm-id">
            <div class="wc-pm-name">${esc(p.name)}</div>
            <div class="wc-pm-meta-row">${flagLogo}<span class="wc-pm-country">${esc(countryFull)}</span><span class="wc-pm-pos pos-${p.position}">${p.position}</span></div>
            <div class="wc-pm-value">${formatBudget(p.budget)}</div>
          </div>
        </div>
        ${this.playerFixtureHtml(id)}
        ${this.playerBreakdownHtml(id)}
        <div class="wc-pm-actions">${actions}</div>
      </div>`;
    document.body.appendChild(overlay);
    this.modal = overlay;
    const act = (fn: () => void): void => { this.closeModal(); fn(); };
    overlay.querySelector('[data-act="swap"]')?.addEventListener('click', () => act(() => this.startSwap(id)));
    overlay.querySelector('[data-act="remove"]')?.addEventListener('click', () => act(() => this.removePlayer(id)));
    overlay.querySelector('[data-act="cap"]')?.addEventListener('click', () => act(() => this.setCaptain(id)));
    overlay.querySelector('[data-act="vice"]')?.addEventListener('click', () => act(() => this.setVice(id)));
    overlay.querySelector('[data-act="close"]')?.addEventListener('click', () => this.closeModal());
    overlay.addEventListener('click', (e) => { if (e.target === overlay) this.closeModal(); });
  }

  // ─── league ───────────────────────────────────────────────────────────────

  private renderLeague(): void {
    const idOf = (userId: string) => {
      const s = this.savedFor(userId);
      return { userId, name: s?.name?.trim() || this.defaultTeamName(userId), primary: s?.primaryColor ?? DEFAULT_PRIMARY, secondary: s?.secondaryColor ?? DEFAULT_SECONDARY, pts: this.totalFor(userId) };
    };
    const rows = [idOf(this.youId), idOf(this.opponentId)].sort((a, b) => b.pts - a.pts);
    const started = rows.some((r) => r.pts > 0);
    this.body().innerHTML = `
      <div class="league-ladder">
        ${rows.map((r, i) => `
          <div class="league-row tappable" data-wc-row="${esc(r.userId)}" role="button" tabindex="0">
            <span class="league-rank">${i + 1}</span>
            <span class="league-id">${shieldSvg(r.primary, r.secondary, 'sm')}<span class="league-name">${esc(r.name)}</span></span>
            <span class="league-pts">${r.pts}<small>pts</small></span>
          </div>`).join('')}
      </div>
      ${started ? '' : '<p class="wc-loading">Points start once the tournament kicks off.</p>'}`;
    // Tap a ladder row → view that manager's team (own ↔ opponent).
    this.body().querySelectorAll<HTMLElement>('[data-wc-row]').forEach((el) => {
      const open = (): void => this.viewManager(el.dataset.wcRow!);
      el.addEventListener('click', open);
      el.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } });
    });
  }
}

/** Pitch markings (reused 11-a-side green lines), inlined to avoid importing
 *  from the FPL module. */
const POS_ORDER_HTML = `<svg class="fpl-pitch-lines" viewBox="0 0 100 130" preserveAspectRatio="none" aria-hidden="true">
  <g fill="none" stroke="#fff" stroke-width="0.5">
    <rect x="2" y="2" width="96" height="126"/><line x1="2" y1="65" x2="98" y2="65"/><circle cx="50" cy="65" r="11"/>
    <rect x="28" y="2" width="44" height="18"/><rect x="40" y="2" width="20" height="7"/><path class="fpl-pitch-d" d="M42 20 A10 10 0 0 1 58 20"/>
    <rect x="28" y="110" width="44" height="18"/><rect x="40" y="121" width="20" height="7"/><path class="fpl-pitch-d" d="M42 110 A10 10 0 0 0 58 110"/>
  </g></svg><div class="fpl-pitch-dim"></div>`;
